<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import type { TooltipContentEmits, TooltipContentProps } from 'reka-ui'
import { TooltipArrow, TooltipContent, TooltipPortal, useForwardPropsEmits } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cn } from '../../../lib/utils'

/**
 * Rebranded from the shadcn recipe, which paints the tooltip `bg-foreground text-background` — on this
 * theme that is a dirty-white slab on a black console, and BG §18.3's ratio (70% charcoal, 10% dirty white)
 * says the surface is charcoal and dirty white is the text on it. So: a ZhPanel in miniature — system
 * charcoal, hairline border, square (BG §17.2 rules out the rounded lockup) — with BG §19.3's mono, because
 * what a tooltip carries here is operational metadata.
 *
 * `whitespace-pre-line` because several of the hints this replaced were multi-line strings built with
 * `[…].filter(Boolean).join('\n')` — a native `title` honours those newlines and a styled box will not
 * unless told to. It changes nothing for the single-line majority.
 *
 * The arrow is filled with the surface colour and carries no border of its own. At 10x5px the missing
 * hairline along its two outer edges does not read, and the alternative (a rotated square showing all
 * four borders) does.
 */
defineOptions({
    inheritAttrs: false,
})

const props = withDefaults(defineProps<TooltipContentProps & { class?: HTMLAttributes['class'] }>(), {
    sideOffset: 6,
})

const emits = defineEmits<TooltipContentEmits>()

const delegatedProps = reactiveOmit(props, 'class')
const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <TooltipPortal>
    <TooltipContent
      data-slot="tooltip-content"
      v-bind="{ ...forwarded, ...$attrs }"
      :class="cn('data-open:animate-in data-open:fade-in-0 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-closed:animate-out data-closed:fade-out-0 inline-flex items-center gap-1.5 whitespace-pre-line rounded-none border border-border bg-[var(--system-charcoal)] px-2.5 py-1.5 font-mono text-[0.6875rem] leading-relaxed tracking-[var(--tracking-system)] text-[var(--dirty-white)] has-data-[slot=kbd]:pr-1.5 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-none z-50 w-fit max-w-xs origin-(--reka-tooltip-content-transform-origin)', props.class)"
    >
      <slot />

      <TooltipArrow class="z-50 size-2.5 rotate-45 rounded-none bg-[var(--system-charcoal)] fill-[var(--system-charcoal)] translate-y-[calc(-50%_-_2px)]" />
    </TooltipContent>
  </TooltipPortal>
</template>
